import * as _ from 'lodash'
import { checkUserInfo } from '@/user/helpers/security.helper'
import EventBus from '@/user/event-bus'

const PC_HEADER_HEIGHT = 353
const PC_BANNER_HEIGHT = 215
const MOBILE_HEADER_HEIGHT = 202
const MOBILE_BANNER_HEIGHT = 137

// 부모 포털(cross-origin)의 message 리스너 부착이 cold-cache에서 수 초 지연될 수 있어
// 이 백오프 구간 내내 재시도한다.
const PORTAL_READY_BACKOFF_DELAYS = [0, 100, 300, 700, 1500, 3100, 6300]

function scheduleWithBackoff(fn) {
  const timerIds = PORTAL_READY_BACKOFF_DELAYS.map((delay) => setTimeout(fn, delay))
  return () => timerIds.forEach(clearTimeout)
}

// 플로팅 바는 단발 발송이면 부모 포털 hydration 지연 구간에 유실되므로,
// updateFloatingBar 호출 시 백오프 tick으로 재전송한다. 재호출되면 이전 타이머 취소 후
// 새 슬롯으로 재스케줄하여 중복 postMessage 누적을 방지한다.
let latestFloatingBar = null
let cancelFloatingBarBackoff = null

export function getHeaderHeight(isPC, full) {
  if (full) {
    return isPC ? PC_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT
  }
  return isPC ? PC_BANNER_HEIGHT : MOBILE_BANNER_HEIGHT
}

const allowedOriginPatterns = process.env.VUE_APP_API_ALLOWED_ORIGIN_PATTERNS.split(',') //
  .map((pattern) => pattern.trim())
  .map((pattern) => new RegExp(pattern)) //

export function getHomepageOrigin() {
  return process.env.VUE_APP_CLOUD_HOMEPAGE_TARGET_ORIGIN.trim()
}

export function getCloudIdOrigin() {
  return process.env.VUE_APP_CLOUD_ID_URL
}

async function getCountry() {
  const pathname = await getLocationProperty('pathname')
  const result = pathname.match(/\/(\w+)\//)
  return result[1]
}

const templateOptions = { interpolate: /{{([\s\S]+?)}}/g }

async function buildHomepagePath(route) {
  const country = await getCountry()
  const homepagePathTemplate = _.template(route.meta.homepagePath, templateOptions)
  const processedTemplate = homepagePathTemplate(route)
  return `/${country}${processedTemplate}`
}

function buildQueryString(route) {
  const queries = Object.entries(route.query)
    .map((e) => e.join('='))
    .join('&')
  return queries ? `?${queries}` : ''
}

function buildIframeUrl(homepagePath, queryString, withOrigin) {
  const parentOrigin = getHomepageOrigin()
  return withOrigin
    ? `${parentOrigin}${homepagePath}${queryString}`
    : `${homepagePath}${queryString}`
}

function buildStandaloneUrl(route) {
  return window.location.origin + route.fullPath
}

function isRunningInIframe() {
  return parent !== window
}

function hasValidFromRoute(from) {
  return from && from.name
}

function isAuthRequired(route) {
  return route.matched.some((routeInfo) => routeInfo.meta.authRequired)
}

async function handleAuthenticationCheck(to, parentOrigin, homepagePath) {
  const isSignin = await checkUserInfo()

  if (!isSignin && to.query.ismember !== 'false') {
    setTimeout(() => {
      const nextUrl = encodeURIComponent(`${parentOrigin}${homepagePath}`)
      navigateTo(`${process.env.VUE_APP_CLOUD_LOGIN_URL}?nextUrl=${nextUrl}`)
    }, 1)
    return true
  }
  return false
}

function handlePathMismatch(parentOrigin, homepagePath) {
  setTimeout(() => {
    navigateTo(`${parentOrigin}${homepagePath}`)
  }, 1)
  return true
}

export async function homepageRouteHandler(to, from) {
  if (!isRunningInIframe()) {
    return false
  }

  if (!hasValidFromRoute(from)) {
    return false
  }

  const authRequired = isAuthRequired(to)

  const parentPath = await getLocationProperty('pathname')

  const parentOrigin = getHomepageOrigin()

  if (!to.meta.homepagePath) {
    return false
  }

  const homepagePath = await routeToUrl(to, false)

  if (authRequired) {
    const redirected = await handleAuthenticationCheck(to, parentOrigin, homepagePath)
    if (redirected) return true
  }

  if (parentPath !== homepagePath) {
    return handlePathMismatch(parentOrigin, homepagePath)
  }

  return false
}

export async function routeToUrl(route, withOrigin) {
  if (parent !== window) {
    const homepagePath = await buildHomepagePath(route)

    const queryString = buildQueryString(route)

    const finalUrl = buildIframeUrl(homepagePath, queryString, withOrigin)

    return finalUrl
  } else {
    const standaloneUrl = buildStandaloneUrl(route)
    return standaloneUrl
  }
}

export async function signIn(nextPath) {
  const origin = getHomepageOrigin()
  let nextUrl = `${origin}${nextPath}`

  if (isRunningInIframe()) {
    const country = await getCountry()
    nextUrl = `${origin}/${country}${nextPath}`
  }
  await signInWithUrl(nextUrl)
}

export async function signInWithUrl(nextUrl) {
  navigateTo(`${process.env.VUE_APP_CLOUD_LOGIN_URL}?nextUrl=${encodeURIComponent(nextUrl)}`)
}

function notifyResize() {
  if (window === parent) {
    return
  }

  const height = document.querySelector('#app').offsetHeight

  parent.postMessage(
    {
      type: 'edu-api',
      api: 'resizeHeight',
      params: [height],
    },
    getHomepageOrigin()
  )
}

/**
 *
 * @type {function}
 */
export const notifyUpdate = _.debounce(() => {
  notifyResize()
}, 100)
/**
 *
 * @type {function}
 */
export const notifyMount = _.debounce(() => {
  scheduleWithBackoff(notifyResize)
}, 100)

export function navigateTo(url) {
  if (window === parent) {
    location.href = url
  } else {
    parent.postMessage(
      {
        type: 'edu-api',
        api: 'navigateToEdu',
        params: [url],
      },
      getHomepageOrigin()
    )
  }
}

export function scrollTo(x, y, behavior = 'auto') {
  if (window === parent) {
    window.scrollTo({
      top: y,
      left: x,
      behavior: behavior,
    })
  } else {
    parent.postMessage(
      {
        type: 'edu-api',
        api: 'windowScrollTo',
        params: [x, y, behavior],
      },
      getHomepageOrigin()
    )
  }
}

export function queryHashOffsetTop(selector) {
  let offsetTop
  if (selector) {
    const hashEl = document.querySelector(selector)
    if (hashEl) {
      offsetTop = hashEl.offsetTop
    }
  }
  parent.postMessage(
    {
      type: 'edu-api',
      api: 'queryHashOffsetTopResponse',
      params: [offsetTop],
    },
    getHomepageOrigin()
  )
}

export function getLocationProperty(property) {
  return new Promise((resolve) => {
    if (window === parent) {
      resolve(location[property])
    } else {
      const responseHandler = (offsetTop) => {
        EventBus.off('edu:get-location-property-response', responseHandler)
        resolve(offsetTop)
      }
      EventBus.on('edu:get-location-property-response', responseHandler)
      parent.postMessage(
        {
          type: 'edu-api',
          api: 'getLocationProperty',
          params: [property],
        },
        getHomepageOrigin()
      )
    }
  })
}

export function updateFloatingBar(data) {
  if (window === parent) return

  latestFloatingBar = {
    type: 'edu-api',
    api: 'updateFloatingBar',
    params: [data],
  }

  cancelFloatingBarBackoff?.()
  cancelFloatingBarBackoff = scheduleWithBackoff(() => {
    if (latestFloatingBar) {
      parent.postMessage(latestFloatingBar, getHomepageOrigin())
    }
  })
}

export function onFloatingBarClick(callback) {
  EventBus.on('edu:floating-bar-click', callback)
}

export function offFloatingBarClick(callback) {
  EventBus.off('edu:floating-bar-click', callback)
}

export function setModalDimmed(isOpen) {
  if (window === parent) return

  parent.postMessage(
    {
      type: 'edu-api',
      api: 'setModalDimmed',
      params: [isOpen],
    },
    getHomepageOrigin()
  )
}

function getLocationPropertyResponse(value) {
  EventBus.emit('edu:get-location-property-response', value)
}

function refreshLocation() {
  location.replace(location.href)
}

function floatingBarClick() {
  EventBus.emit('edu:floating-bar-click')
}

const apis = {
  queryHashOffsetTop,
  getLocationPropertyResponse,
  refreshLocation,
  floatingBarClick,
}

function onReceiveMessage(event) {
  const allowed = allowedOriginPatterns.findIndex((pattern) => pattern.test(event.origin)) > -1
  if (!allowed) {
    return
  }
  const data = event.data
  if (data.type !== 'edu-api') {
    return
  }
  const api = apis[data.api]
  if (!api) {
    return
  }
  api.apply(null, data.params || [])
}

window.addEventListener('message', onReceiveMessage, false) // NOSONAR javascript:S2819 잘 반영 되어 있음
